import { useRef } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormItem, FormLabel } from "@/components/ui/form";
import { useUploadMatchCardPhoto } from "@/lib/api/user";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { useToast } from "@/components/ui/use-toast";

const MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024; // 15 MB

interface MatchCardPhotoSectionProps {
  userId: string;
  photoUrl?: string;
}

export function MatchCardPhotoSection({
  userId,
  photoUrl,
}: MatchCardPhotoSectionProps) {
  const uploadMatchCardPhoto = useUploadMatchCardPhoto(userId);
  const inputRef = useRef<HTMLInputElement>(null);
  const { url: signedUrl } = useSignedMedia(photoUrl, userId);
  const { toast } = useToast();

  return (
    <FormItem>
      <FormLabel className="text-sm font-medium">Match Card Photo</FormLabel>
      <div className="space-y-3">
        {signedUrl && (
          <div className="aspect-[3/4] max-w-[160px] overflow-hidden rounded-lg border">
            <img
              src={signedUrl}
              alt="Match card photo"
              className="h-full w-full object-cover"
            />
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            if (!/^image\/(jpeg|png)$/.test(file.type)) {
              toast({
                title: "Invalid file type",
                description: "Please upload a JPEG or PNG image.",
                variant: "destructive",
              });
              return;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
              toast({
                title: "File too large",
                description: "Image must be under 15 MB.",
                variant: "destructive",
              });
              return;
            }
            await uploadMatchCardPhoto.mutateAsync(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="w-full"
          disabled={uploadMatchCardPhoto.isPending}
        >
          {uploadMatchCardPhoto.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : signedUrl ? (
            "Change Photo"
          ) : (
            "Upload Photo"
          )}
        </Button>
      </div>
    </FormItem>
  );
}